import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CreditCard, Loader2, Smartphone, Ban } from "lucide-react";
import { toast } from "sonner";
import TerminalSalePanel from "@/components/TerminalSalePanel";

export default function BalanceDuePanel({ sessionId, onBalance, onWriteOff, onCollected = () => {} }) {
  const [bal, setBal] = useState(null);
  const [busy, setBusy] = useState(null);
  const [sale, setSale] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/sessions/${sessionId}/balance`);
    setBal(data);
    onBalance(data.days);
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load().catch(() => onBalance(0)); }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!bal || (bal.days <= 0 && !bal.collections?.length)) return null;

  if (sale) {
    return (
      <div className="rounded-lg border border-slate-700 bg-slate-950 p-3">
        <TerminalSalePanel
          sale={sale}
          onUpdate={setSale}
          onApproved={() => { toast.success("Balance collected on terminal"); onCollected(sale.amount); setSale(null); load(); }}
          onClose={() => { setSale(null); load(); }}
        />
      </div>
    );
  }

  const chargeToken = async () => {
    setBusy("token");
    try {
      const { data } = await api.post(`/sessions/${sessionId}/balance/charge-token`);
      if (data.ok) { toast.success(`Saved card charged $${data.amount.toFixed(2)} · ${data.approvalCode || ""}`); onCollected(data.amount); }
      else toast.error(`Saved card declined: ${data.message}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Charge failed");
    } finally {
      setBusy(null);
    }
  };

  const terminal = async () => {
    setBusy("terminal");
    try {
      const { data } = await api.post(`/sessions/${sessionId}/balance/terminal`);
      setSale(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start terminal sale");
    } finally {
      setBusy(null);
    }
  };

  if (bal.days <= 0) {
    return (
      <div data-testid="balance-settled" className="rounded-lg border border-emerald-500/40 bg-emerald-950/30 px-4 py-3 text-sm text-emerald-200">
        Balance settled — {bal.collections.reduce((a, c) => a + c.days, 0)} unpaid day(s) collected
        (${bal.collections.reduce((a, c) => a + c.amount, 0).toFixed(2)}).
      </div>
    );
  }

  return (
    <div data-testid="balance-due-panel" className="space-y-3 rounded-lg border-2 border-amber-500/60 bg-amber-950/30 p-4">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
        <div className="flex-1">
          <p className="font-heading text-lg font-bold text-amber-200">Balance Due</p>
          <p className="text-xs text-amber-200/80">
            {bal.days} unpaid day{bal.days > 1 ? "s" : ""} ({bal.failedCycles} declined cycle{bal.failedCycles === 1 ? "" : "s"},
            {" "}{bal.overtimeHours}h past paid-through · partial days round up)
          </p>
        </div>
        <p data-testid="balance-due-total" className="font-data text-2xl font-black text-amber-100">${bal.total.toFixed(2)}</p>
      </div>
      <div className="font-data text-xs text-amber-200/70">
        {bal.days} × daily rate = ${bal.base.toFixed(2)} + tax ({bal.taxPercent}%) ${bal.tax.toFixed(2)}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button data-testid="balance-charge-token" onClick={chargeToken} disabled={!!busy || !bal.hasToken}
          className="h-12 gap-2 bg-[#A0148C] font-bold hover:bg-[#80106F]">
          {busy === "token" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Charge saved card
        </Button>
        <Button data-testid="balance-charge-terminal" onClick={terminal} disabled={!!busy} variant="outline"
          className="h-12 gap-2 border-slate-600 bg-slate-950 font-bold text-slate-100 hover:bg-slate-800">
          {busy === "terminal" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Smartphone className="h-4 w-4" />} New card on terminal
        </Button>
      </div>
      {bal.canWriteOff && (
        <Button data-testid="balance-write-off" variant="ghost" disabled={!!busy}
          onClick={() => window.confirm(`Write off $${bal.total.toFixed(2)} and check out? This is logged under your name.`) && onWriteOff()}
          className="h-10 w-full gap-2 text-sm text-red-300 hover:bg-red-950/40 hover:text-red-200">
          <Ban className="h-4 w-4" /> Owner: write off balance &amp; check out
        </Button>
      )}
    </div>
  );
}
